import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Compass, TrendingUp, Map, Sparkles, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api, { setAuthToken } from '../utils/api';
import { toast } from 'sonner';

export const Landing = () => {
  const navigate = useNavigate();
  const { language, changeLanguage, t } = useLanguage();

  // Handle Google OAuth callback if code is present in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      console.log('Google OAuth code detected in Landing page');
      // Clear the URL parameters
      window.history.replaceState({}, document.title, '/');
      
      // Exchange code for token
      const handleGoogleCallback = async () => {
        try {
          console.log('Exchanging Google code with redirect_uri:', window.location.origin);
          const response = await api.post('/auth/google/exchange', {
            code: code,
            redirect_uri: window.location.origin
          });
          console.log('Google exchange successful, setting token');
          setAuthToken(response.data.token);
          toast.success(language === 'tr' ? 'Google ile giriş başarılı!' : 'Signed in with Google!');
          navigate('/dashboard');
        } catch (error) {
          console.error('Google exchange failed:', error.response?.data || error.message);
          toast.error(error.response?.data?.detail || 'Google authentication failed');
          navigate('/auth');
        }
      };
      
      handleGoogleCallback();
    }
  }, [navigate, language]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Language Toggle - Top Right */}
      <div className="fixed top-6 right-6 z-50 flex gap-2">
        <button
          onClick={() => changeLanguage('en')}
          className={`px-4 py-2 rounded-full border transition-all font-sans text-sm font-semibold flex items-center gap-2 ${
            language === 'en'
              ? 'border-[#D4AF37] bg-[#D4AF37] text-[#030305]'
              : 'border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:border-[#D4AF37]'
          }`}
          data-testid="lang-en"
        >
          <Globe className="w-4 h-4" />
          EN
        </button>
        <button
          onClick={() => changeLanguage('tr')}
          className={`px-4 py-2 rounded-full border transition-all font-sans text-sm font-semibold flex items-center gap-2 ${
            language === 'tr'
              ? 'border-[#D4AF37] bg-[#D4AF37] text-[#030305]'
              : 'border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:border-[#D4AF37]'
          }`}
          data-testid="lang-tr"
        >
          <Globe className="w-4 h-4" />
          TR
        </button>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <Compass className="w-20 h-20 text-[#D4AF37] mx-auto mb-6" />
            </div>
            <h1
              className="font-serif font-light tracking-tight text-[#F8F5F2] text-5xl sm:text-6xl lg:text-7xl mb-6"
              data-testid="landing-title"
            >
              {t('landing.title')}
            </h1>
            <p className="font-sans font-light leading-relaxed text-[#D1D1D6] text-xl sm:text-2xl mb-12 max-w-3xl mx-auto">
              {t('landing.subtitle')}
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg"
              data-testid="get-started-button"
            >
              {t('landing.cta')}
            </button>
          </motion.div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="relative z-10 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-serif font-light text-4xl text-[#F8F5F2] text-center mb-16"
          >
            {t('landing.howItWorks')}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-panel rounded-2xl p-8"
              data-testid="step-assessment"
            >
              <Map className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-2xl text-[#F8F5F2] mb-4">{t('landing.step1Title')}</h3>
              <p className="font-sans font-light text-[#D1D1D6] leading-relaxed">
                {t('landing.step1Desc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-panel rounded-2xl p-8"
              data-testid="step-guidance"
            >
              <Sparkles className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-2xl text-[#F8F5F2] mb-4">{t('landing.step2Title')}</h3>
              <p className="font-sans font-light text-[#D1D1D6] leading-relaxed">
                {t('landing.step2Desc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-panel rounded-2xl p-8"
              data-testid="step-progress"
            >
              <TrendingUp className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-2xl text-[#F8F5F2] mb-4">{t('landing.step3Title')}</h3>
              <p className="font-sans font-light text-[#D1D1D6] leading-relaxed">
                {t('landing.step3Desc')}
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Philosophy Section */}
      <div className="relative z-10 py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-serif text-3xl text-[#D4AF37] mb-8 italic">
              {t('landing.philosophy')}
            </p>
            <p className="font-sans font-light text-[#D1D1D6] text-lg">
              {t('landing.philosophyDesc')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-6 text-center">
          <button
            onClick={() => navigate('/about')}
            className="font-sans text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          >
            {language === 'tr' ? 'Hakkında' : 'About'}
          </button>
          <span className="hidden sm:inline text-[#3f3f46]">·</span>
          <button
            onClick={() => navigate('/privacy')}
            className="font-sans text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          >
            {language === 'tr' ? 'Gizlilik' : 'Privacy'}
          </button>
          <span className="hidden sm:inline text-[#3f3f46]">·</span>
          <a
            href="mailto:rayrizz@gmail.com"
            className="font-sans text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          >
            {language === 'tr' ? 'İletişim' : 'Contact'}
          </a>
        </div>
        <p className="text-center text-[#6B7280] text-xs font-sans mt-6">
          © 2026 IN — Inner Navigation
        </p>
      </footer>
    </div>
  );
};

export default Landing;